// src/pages/B2B/crm/components/VenueContactList.tsx
import React, { useState } from 'react';
import { VenueContact, ContactKind, CONTACT_KINDS } from '../api/types';
import { VenueContactRow } from './VenueContactRow';

interface Props {
  contacts: VenueContact[];
  onCreate: (c: { name: string; kind: ContactKind } & Partial<VenueContact>) => Promise<void>;
  onUpdate: (id: string, patch: Partial<VenueContact>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const KIND_LABEL: Record<ContactKind, string> = {
  decision_maker: 'Decision Makers',
  on_site: 'On-Site',
};

const KIND_ADD_LABEL: Record<ContactKind, string> = {
  decision_maker: 'Decision Maker',
  on_site: 'On-Site Contact',
};

type Draft = { name: string; title: string; phone: string; email: string };
const EMPTY_DRAFT: Draft = { name: '', title: '', phone: '', email: '' };

export function VenueContactList({ contacts, onCreate, onUpdate, onDelete }: Props) {
  const [addingKind, setAddingKind] = useState<ContactKind | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);

  const grouped: Record<ContactKind, VenueContact[]> = { decision_maker: [], on_site: [] };
  for (const c of contacts) grouped[c.kind].push(c);

  const cancel = () => { setAddingKind(null); setDraft(EMPTY_DRAFT); };

  return (
    <div>
      {CONTACT_KINDS.map(kind => (
        <div key={kind} className="mb-3">
          <div className="text-violet-400 text-[11px] font-semibold uppercase tracking-wider mb-1">{KIND_LABEL[kind]}</div>
          <div className="bg-[#0f0f1a] border border-[#2a2a3e] rounded-md px-3">
            {grouped[kind].length === 0 && addingKind !== kind && (
              <div className="py-2 text-gray-500 text-xs">None</div>
            )}
            {grouped[kind].map(c => (
              <VenueContactRow
                key={c.id}
                contact={c}
                onSave={(patch) => onUpdate(c.id, patch)}
                onDelete={() => onDelete(c.id)}
              />
            ))}

            {addingKind === kind ? (
              <div className="py-2 space-y-1.5 border-t border-[#1f1f33]">
                <input
                  autoFocus
                  className="w-full bg-[#15152a] border border-[#2a2a3e] rounded px-2 py-1 text-sm text-gray-200"
                  placeholder="Name"
                  value={draft.name}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                />
                <input
                  className="w-full bg-[#15152a] border border-[#2a2a3e] rounded px-2 py-1 text-sm text-gray-200"
                  placeholder="Title"
                  value={draft.title}
                  onChange={e => setDraft({ ...draft, title: e.target.value })}
                />
                <div className="flex gap-1">
                  <input
                    className="flex-1 bg-[#15152a] border border-[#2a2a3e] rounded px-2 py-1 text-sm text-gray-200"
                    placeholder="Phone"
                    value={draft.phone}
                    onChange={e => setDraft({ ...draft, phone: e.target.value })}
                  />
                  <input
                    className="flex-1 bg-[#15152a] border border-[#2a2a3e] rounded px-2 py-1 text-sm text-gray-200"
                    placeholder="Email"
                    value={draft.email}
                    onChange={e => setDraft({ ...draft, email: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-2 text-xs">
                  <button onClick={cancel} className="text-gray-400">Cancel</button>
                  <button
                    onClick={async () => {
                      if (!draft.name) return;
                      setBusy(true);
                      try {
                        await onCreate({
                          name: draft.name,
                          title: draft.title || undefined,
                          phone: draft.phone || undefined,
                          email: draft.email || undefined,
                          kind,
                        });
                        cancel();
                      } finally { setBusy(false); }
                    }}
                    disabled={busy || !draft.name}
                    className="text-violet-400 hover:text-violet-300 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setAddingKind(kind); setDraft(EMPTY_DRAFT); }}
                className="w-full text-left text-violet-400 hover:text-violet-300 text-xs py-2"
              >
                + Add {KIND_ADD_LABEL[kind]}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
